import { AccountType } from "shared/types/accounts";
import { User } from "features/user/types";
import {
  getAccountPlatformType,
  isLinkedinCompanyAccount,
} from "entities/accounts/libs/account-platform";
import { hasEntitlement } from "utils/has-entitlement";
import { isReverseTrial } from "utils/reverse-trial";

/**
 * The "social set only" entitlement model.
 *
 * When a customer's plan has the `social-set-only` Chargebee feature switch
 * turned on, they don't have a numeric account quota. Instead they get one
 * account per platform — the "social set" — covering every channel Sked
 * supports. Notification channels (Snapchat, Other, IG-personal, Threads-
 * notification, TikTok-notification) remain unlimited and are not part of
 * the slot model.
 *
 * Sub-type rules:
 *  - Facebook is **strict** — one FB total whether Page or Group
 *  - LinkedIn is **split** — one LI Personal AND one LI Company allowed
 *
 * Reverse trial users always get unlimited access (the reverse-trial bypass
 * fires before the social-set check anywhere this matters).
 */

export type SocialSetSlotKey =
  | "IG"
  | "FB"
  | "TW"
  | "LI:personal"
  | "LI:company"
  | "GMB"
  | "YT"
  | "PI"
  | "TT"
  | "TH";

/** Canonical ordered list of slots, used for rendering tile grids. */
export const SOCIAL_SET_SLOTS: readonly SocialSetSlotKey[] = [
  "IG",
  "FB",
  "TW",
  "LI:personal",
  "LI:company",
  "GMB",
  "YT",
  "PI",
  "TT",
  "TH",
] as const;

/** Human-readable labels used in tooltips, alerts, and counters. */
export const SOCIAL_SET_SLOT_LABELS: Record<SocialSetSlotKey, string> = {
  IG: "Instagram",
  FB: "Facebook",
  TW: "X",
  "LI:personal": "LinkedIn (Personal)",
  "LI:company": "LinkedIn (Company)",
  GMB: "Google Business Profile",
  YT: "YouTube",
  PI: "Pinterest",
  TT: "TikTok",
  TH: "Threads",
};

/**
 * Map an account to the slot it occupies, or null if it doesn't count toward
 * the social set (notification channels and unrecognised platforms).
 */
export const getAccountSlotKey = (
  account: AccountType
): SocialSetSlotKey | null => {
  // Notification-only accounts are identified by missing platformId on the
  // notification-style platforms (TT/TH) plus the always-notification ones
  // (SC/OT). They never count toward the social set slots.
  const platformType = getAccountPlatformType(account);

  switch (platformType) {
    case "IG": {
      // Instagram Personal accounts have no API token (`igTokenAddedAt`) — they
      // live in the notification channels section of the connect dialog and
      // must not consume the single IG Business slot. Only API-connected IG
      // Business accounts count toward the social set.
      return account.igTokenAddedAt ? "IG" : null;
    }
    case "FB": {
      return "FB";
    } // Page OR Group → same slot (strict rule)
    case "TW": {
      return "TW";
    }
    case "LI": {
      return isLinkedinCompanyAccount(account) ? "LI:company" : "LI:personal";
    }
    case "GMB": {
      return "GMB";
    }
    case "YT": {
      return "YT";
    }
    case "PI": {
      return "PI";
    }
    case "TT": {
      // TikTok notification accounts have no platformId; only auto-posting
      // (true API-connected) TT accounts occupy the slot.
      return account.platformId ? "TT" : null;
    }
    case "TH": {
      // Same as TikTok — only API-connected Threads count toward the slot.
      return account.platformId ? "TH" : null;
    }
    case "SC":
    case "OT": {
      // Pure notification channels — never count toward slots.
      return null;
    }
    default: {
      return null;
    }
  }
};

/** Set of slot keys currently occupied by the user's accounts. */
export const getUsedSocialSetSlots = (
  accounts: AccountType[]
): Set<SocialSetSlotKey> => {
  const used = new Set<SocialSetSlotKey>();
  for (const account of accounts) {
    const slot = getAccountSlotKey(account);
    if (slot) used.add(slot);
  }
  return used;
};

/**
 * Primitive: given a pre-computed `used` set, return the complementary set
 * of still-available slots. Pure set complement against `SOCIAL_SET_SLOTS`.
 *
 * Used directly by memoized selectors chained off `getSocialSetUsedSlots`
 * so they don't need to re-traverse the accounts list. Regular callers
 * should prefer `getAvailableSocialSetSlots(accounts)` below.
 */
export const getAvailableSocialSetSlotsFromUsed = (
  used: Set<SocialSetSlotKey>
): Set<SocialSetSlotKey> => {
  const available = new Set<SocialSetSlotKey>();
  for (const slot of SOCIAL_SET_SLOTS) {
    if (!used.has(slot)) available.add(slot);
  }
  return available;
};

/** Set of slot keys still available (not yet occupied by `accounts`). */
export const getAvailableSocialSetSlots = (
  accounts: AccountType[]
): Set<SocialSetSlotKey> =>
  getAvailableSocialSetSlotsFromUsed(getUsedSocialSetSlots(accounts));

/** True if every slot is filled. */
export const isSocialSetFull = (accounts: AccountType[]): boolean => {
  return getUsedSocialSetSlots(accounts).size >= SOCIAL_SET_SLOTS.length;
};

/**
 * True if a specific slot still has room.
 *
 * Cosmetic-only client-side check used to disable individual platform tiles in
 * the connect dialog. The server's social-set entitlement enforcement is the
 * source of truth — if two connect flows race past this check the second one
 * will be rejected by the API.
 */
export const isSlotAvailable = (
  accounts: AccountType[],
  slot: SocialSetSlotKey
): boolean => {
  return !getUsedSocialSetSlots(accounts).has(slot);
};

/**
 * True if the user is on the social-set-only plan model.
 *
 * Reverse trial users always get unlimited access, so this returns false
 * during a reverse trial — the trial bypass should win at every gate.
 */
export const isSocialSetOnly = (user: User | null | undefined): boolean => {
  if (!user) return false;
  if (isReverseTrial(user)) return false;
  return hasEntitlement(user, "social-set-only");
};

/**
 * True if a social-set-only user has filled every available slot. The only
 * place "limit reached" applies in this model.
 */
export const isReachedSocialSetLimit = (
  user: User | null | undefined,
  accounts: AccountType[]
): boolean => {
  return isSocialSetOnly(user) && isSocialSetFull(accounts);
};

/**
 * Format a set of available slots into a human sentence fragment for use in
 * "You can still add: …" copy. Uses serial commas with "and" before the last
 * item. Returns an empty string if the set is empty.
 *
 * LinkedIn personal/company are internal Sked distinctions — when both are
 * still available we collapse them to a single "LinkedIn" so the copy reads
 * naturally to the customer. If only one of the two is open we keep the
 * suffix so the user knows which kind they can still add.
 *
 *   formatRemainingSlots(new Set(["TW"])) → "X"
 *   formatRemainingSlots(new Set(["TW", "TT"])) → "X and TikTok"
 *   formatRemainingSlots(new Set(["TW", "TT", "TH"])) → "X, TikTok, and Threads"
 *   formatRemainingSlots(new Set(["LI:personal", "LI:company"])) → "LinkedIn"
 */
export const formatRemainingSlots = (
  available: Set<SocialSetSlotKey>
): string => {
  const bothLinkedinOpen =
    available.has("LI:personal") && available.has("LI:company");

  // Preserve canonical order for stable copy. When both LinkedIn slots are
  // open we drop the company entry and rename the personal entry to the
  // un-suffixed "LinkedIn" label.
  const ordered: string[] = [];
  for (const slot of SOCIAL_SET_SLOTS) {
    if (!available.has(slot)) continue;
    if (bothLinkedinOpen && slot === "LI:company") continue;
    if (bothLinkedinOpen && slot === "LI:personal") {
      ordered.push("LinkedIn");
    } else {
      ordered.push(SOCIAL_SET_SLOT_LABELS[slot]);
    }
  }

  if (ordered.length === 0) return "";
  if (ordered.length === 1) return ordered[0];
  if (ordered.length === 2) return `${ordered[0]} and ${ordered[1]}`;
  const head = ordered.slice(0, -1).join(", ");
  // Use index access rather than `.at(-1)` so the bundled output stays
  // compatible with the project's es2020 compile target without relying on
  // the polyfilled lib types.
  // eslint-disable-next-line unicorn/prefer-at
  return `${head}, and ${ordered[ordered.length - 1]}`;
};
